import React, { useState, useEffect } from 'react'
import { Flex, Text } from 'theme-ui'
import { Input, Svg } from 'ui'
import { clearError } from 'store/session'
import { getStarted } from 'components/HomePage/Header/menus'
import { useAppDispatch } from 'store/hooks'
import {
  TitleSection,
  MainContentContainer,
  StaticPageBody,
  StaticPageHeader,
  StaticPageWrapper,
} from 'components/NewStaticPage'
import { getDocumentHead } from 'components/DocumentHead'
import { MenuItem, GetStartedDropdown } from 'components/HomePage/Header'
import CheckIcon from 'images/organization/homepage/sections/industries/check.svg'
import { useForgotPasswordMutation } from 'store/api/user'
import { SerializedError } from '@reduxjs/toolkit'
import { LoginSignUpButton } from 'components/LoginSignUpForm/LoginSignUpButton'

export default function ForgotPassword(): React.ReactElement {
  const [email, setEmail] = useState('')
  const dispatch = useAppDispatch()
  const [passwordForgot, { error, isSuccess, isLoading }] = useForgotPasswordMutation()

  useEffect(() => {
    // clear the error if navigating between pages
    // https://gitlab.int.windscribe.com/controld/frontend/-/issues/320
    dispatch(clearError())
  }, [dispatch])

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    passwordForgot({ email })
  }

  return (
    <StaticPageWrapper>
      <StaticPageHeader>
        <Flex
          sx={{
            width: '100%',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: ['0.8rem', '1.6rem'],
          }}
        >
          <MenuItem text="Log in" to="/login" />
          <GetStartedDropdown dropdownItems={getStarted}>Get Started</GetStartedDropdown>
        </Flex>
      </StaticPageHeader>
      <StaticPageBody>
        <TitleSection error={(error as SerializedError)?.message} isLoading={isLoading}>
          {isSuccess ? 'Check your Inbox' : 'Forgot Password'}
        </TitleSection>
        <MainContentContainer>
          <Flex
            sx={{
              width: ['100%', '63rem'],
              py: '4rem',
              px: ['1.6rem', '4rem'],
            }}
          >
            {isSuccess ? (
              <Flex
                sx={{
                  gap: '1.2rem',
                  alignItems: 'center',
                }}
              >
                <Svg
                  svg={CheckIcon}
                  sx={{ width: '2.4rem', height: '2.4rem', flexShrink: 0 }}
                  descriptiveText="Success check icon"
                />
                <Text
                  sx={{
                    textAlign: 'left',
                    color: 'lightSteelBlue',
                    fontSize: '1.5rem',
                    lineHeight: '138%',
                    letterSpacing: '0.06rem',
                  }}
                >
                  If an account exists under the email you provided, expect an email with
                  instructions shortly.
                </Text>
              </Flex>
            ) : (
              <Flex
                as="form"
                className="b2b-input-autofill"
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 'auto',
                  justifyContent: 'center',
                }}
                data-testid="login-form"
                onSubmit={(e: React.FormEvent): void => handleSubmit(e)}
              >
                <Flex
                  sx={{
                    flexDirection: 'column',
                    gap: '2.4rem',
                    width: ['100%', '48rem'],
                  }}
                >
                  <Input
                    color="white"
                    label="Email"
                    id="email"
                    type="email"
                    name="email"
                    data-testid="forgot-password-input"
                    aria-label="Email"
                    onChange={(e): void => setEmail(e.target.value)}
                    hasText={!!email}
                  />
                  <LoginSignUpButton
                    data-testid="reset-link-button"
                    ariaLabel="Send Reset Link Button"
                    isLoading={isLoading}
                    disabled={!email || isLoading}
                  >
                    Send Reset Link
                  </LoginSignUpButton>
                </Flex>
              </Flex>
            )}
          </Flex>
        </MainContentContainer>
      </StaticPageBody>
    </StaticPageWrapper>
  )
}

export const Head = getDocumentHead({ title: 'Forgot Password' })
